import { TransportError } from 'ProdantixSdk/core/errors';
import { type FetchLike, makeAbortController, resolveFetch, trimTrailingSlash } from 'ProdantixSdk/core/http';
import type { EventProperties } from 'ProdantixSdk/core/types';

export const SURVEY_SHOWN = '$survey_shown';
export const SURVEY_DISMISSED = '$survey_dismissed';
export const SURVEY_SENT = '$survey_sent';

export type SurveyQuestionKind = 'emoji' | 'link' | 'multiple' | 'nps' | 'open' | 'rating' | 'single';

export interface SurveyBranch {
  choice?: string | null;
  maxScore?: number | null;
  minScore?: number | null;
  next?: string | null;
}

export interface SurveyQuestion {
  branches: SurveyBranch[];
  choices: string[];
  description?: string | null;
  highLabel?: string | null;
  id: string;
  kind: SurveyQuestionKind;
  link?: string | null;
  lowLabel?: string | null;
  optional: boolean;
  prompt: string;
  scale?: number | null;
}

export interface SurveyUrlRule {
  matches: 'contains' | 'exact' | 'starts_with';
  value: string;
}

export interface SurveyAppearance {
  accent?: string | null;
  placement: 'bottom_center' | 'bottom_left' | 'bottom_right';
  submitLabel?: string | null;
  thankYou?: string | null;
}

export interface Survey {
  appearance: SurveyAppearance;
  choiceLabels?: Record<string, string[]>;
  event?: string | null;
  id: string;
  iteration: number;
  kind: 'api' | 'popover';
  name: string;
  questions: SurveyQuestion[];
  showOnce: boolean;
  urls: SurveyUrlRule[];
  waitDays: number;
}

export type SurveyAnswer = number | string | string[];

export interface SurveyMemory {
  lastShownAt?: number;
  settled: string[];
}

export interface SurveysClientOptions {
  fetch?: FetchLike;
  host: string;
  projectKey: string;
  requestTimeoutMs?: number;
}

export class SurveysClient {
  private readonly fetch?: FetchLike;
  private readonly host: string;
  private readonly projectKey: string;
  private readonly requestTimeoutMs: number;

  constructor(options: SurveysClientOptions) {
    this.fetch = resolveFetch(options.fetch);
    this.host = trimTrailingSlash(options.host);
    this.projectKey = options.projectKey;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 10_000;
  }

  async surveys(distinctId: string, language?: string): Promise<Survey[]> {
    const fetchImpl = this.fetch;
    if (!fetchImpl) {
      throw new TransportError('no fetch implementation available in this runtime');
    }
    const controller = makeAbortController();
    const timer = controller ? setTimeout(() => controller.abort(), this.requestTimeoutMs) : undefined;
    try {
      const response = await fetchImpl(`${this.host}/v1/surveys?distinct_id=${encodeURIComponent(distinctId)}${language ? `&lang=${encodeURIComponent(language)}` : ''}`, {
        headers: { authorization: `Bearer ${this.projectKey}` },
        method: 'GET',
        signal: controller?.signal,
      });
      if (!response.ok) {
        throw new TransportError(`surveys request failed with status ${response.status}`, response.status);
      }
      const parsed = JSON.parse(await response.text()) as { surveys?: Survey[] };
      return parsed.surveys ?? [];
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  }
}

const DAY_MS = 86_400_000;

export const settledKey = (survey: Pick<Survey, 'id' | 'iteration'>): string => `${survey.id}:${survey.iteration}`;

export function urlMatches(rules: SurveyUrlRule[], href: string): boolean {
  if (rules.length === 0) {
    return true;
  }
  return rules.some(rule => {
    switch (rule.matches) {
      case 'exact':
        return href === rule.value;
      case 'starts_with':
        return href.startsWith(rule.value);
      default:
        return href.includes(rule.value);
    }
  });
}

export function isDue(survey: Survey, memory: SurveyMemory, href: string, now: number, event?: string): boolean {
  if (survey.kind !== 'popover' || memory.settled.includes(settledKey(survey))) {
    return false;
  }
  if (survey.showOnce && memory.settled.some(key => key.startsWith(`${survey.id}:`))) {
    return false;
  }
  if (survey.waitDays > 0 && memory.lastShownAt !== undefined && now - memory.lastShownAt < survey.waitDays * DAY_MS) {
    return false;
  }
  if (survey.event && survey.event !== event) {
    return false;
  }
  return urlMatches(survey.urls, href);
}

function inRange(score: number, branch: SurveyBranch, top: number): boolean {
  return score >= (branch.minScore ?? 0) && score <= (branch.maxScore ?? top);
}

export function nextQuestion(survey: Survey, question: SurveyQuestion, answer: SurveyAnswer | undefined): string | null {
  const top = question.kind === 'nps' ? 10 : (question.scale ?? 5);
  const taken = question.branches.find(branch => (branch.choice != null ? answer === branch.choice : typeof answer === 'number' && inRange(answer, branch, top)));
  if (taken) {
    return taken.next ?? null;
  }
  const at = survey.questions.findIndex(candidate => candidate.id === question.id);
  return survey.questions[at + 1]?.id ?? null;
}

export function responseProperties(survey: Survey, answers: Record<string, SurveyAnswer>): EventProperties {
  const properties: EventProperties = { $survey_id: survey.id, $survey_iteration: survey.iteration, $survey_name: survey.name };
  for (const [questionId, answer] of Object.entries(answers)) {
    properties[`$survey_response_${questionId}`] = answer;
  }
  return properties;
}
